import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle,
  Building,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Shield,
  BarChart3,
  Upload,
  Star,
  Award,
  Database,
  Edit
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';
import WorkOrderService from '../../services/workOrderService';
import VendorService from '../../services/vendorService';

// Helper function to map work order data from backend to form data
const mapWorkOrderToFormData = (workOrder) => {
  if (!workOrder) return null;

  return {
    workOrderNumber: workOrder.workOrderNumber || '',
    title: workOrder.title || '',
    description: workOrder.description || '',
    priority: workOrder.priority || 'medium',
    status: workOrder.status || 'draft',
    category: workOrder.category || '',
    workType: workOrder.workType || '',
    location: workOrder.location || '',
    siteAddress: workOrder.siteAddress || '',
    
    assignedTo: (workOrder.assignedTo?._id || (typeof workOrder.assignedTo === 'string' ? workOrder.assignedTo : '')) || '',
    projectManager: (workOrder.projectManager?._id || (typeof workOrder.projectManager === 'string' ? workOrder.projectManager : '')) || '',
    subcontractor: (workOrder.subcontractor?._id || (typeof workOrder.subcontractor === 'string' ? workOrder.subcontractor : '')) || '',
    teamMembers: workOrder.teamMembers?.map(m => {
      if (typeof m === 'string') return m;
      if (m && m._id) return m._id;
      return '';
    }).filter(Boolean) || [],
    requiredSkills: (workOrder.requiredSkills || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    equipment: (workOrder.equipment || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    materials: (workOrder.materials || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    
    startDate: workOrder.startDate ? new Date(workOrder.startDate).toISOString().split('T')[0] : '',
    endDate: workOrder.endDate ? new Date(workOrder.endDate).toISOString().split('T')[0] : '',
    estimatedDuration: workOrder.estimatedDuration || '',
    deadline: workOrder.deadline ? new Date(workOrder.deadline).toISOString().split('T')[0] : '',
    workingHours: workOrder.workingHours || '',
    timeZone: workOrder.timeZone || '',
    
    budget: workOrder.budget ? String(workOrder.budget) : '',
    currency: workOrder.currency || 'USD',
    hourlyRate: workOrder.hourlyRate ? String(workOrder.hourlyRate) : '',
    estimatedCost: workOrder.estimatedCost ? String(workOrder.estimatedCost) : '',
    actualCost: workOrder.actualCost ? String(workOrder.actualCost) : '',
    costBreakdown: {
      labor: workOrder.costBreakdown?.labor ? String(workOrder.costBreakdown.labor) : '',
      materials: workOrder.costBreakdown?.materials ? String(workOrder.costBreakdown.materials) : '',
      equipment: workOrder.costBreakdown?.equipment ? String(workOrder.costBreakdown.equipment) : '',
      travel: workOrder.costBreakdown?.travel ? String(workOrder.costBreakdown.travel) : '',
      other: workOrder.costBreakdown?.other ? String(workOrder.costBreakdown.other) : ''
    },
    
    scopeOfWork: workOrder.scopeOfWork || '',
    deliverables: (workOrder.deliverables || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    milestones: (workOrder.milestones || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    requirements: (workOrder.requirements || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    specifications: workOrder.specifications || '',
    qualityStandards: workOrder.qualityStandards || '',
    
    safetyRequirements: (workOrder.safetyRequirements || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    complianceRequirements: (workOrder.complianceRequirements || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    permits: (workOrder.permits || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    inspections: (workOrder.inspections || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    safetyEquipment: (workOrder.safetyEquipment || []).map(item => 
      typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))
    ).filter(Boolean),
    
    communicationChannels: workOrder.communicationChannels || [],
    reportingFrequency: workOrder.reportingFrequency || 'weekly',
    progressUpdates: workOrder.progressUpdates !== undefined ? workOrder.progressUpdates : true,
    
    risks: workOrder.risks || [],
    mitigationPlans: workOrder.mitigationPlans || [],
    contingencyPlans: workOrder.contingencyPlans || [],
    
    autoNotifications: workOrder.autoNotifications !== undefined ? workOrder.autoNotifications : true,
    progressTracking: workOrder.progressTracking !== undefined ? workOrder.progressTracking : true,
    budgetAlerts: workOrder.budgetAlerts !== undefined ? workOrder.budgetAlerts : true,
    deadlineReminders: workOrder.deadlineReminders !== undefined ? workOrder.deadlineReminders : true,
    customFields: workOrder.customFields || []
  };
};

const CreateWorkOrder = ({ editMode = false, viewMode = false, workOrderId = null }) => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  const [subcontractors, setSubcontractors] = useState([]);
  const [loadingSubcontractors, setLoadingSubcontractors] = useState(false);
  const [users, setUsers] = useState([]);

  // Test mode state
  const [testMode, setTestMode] = useState(false);
  const [loading, setLoading] = useState(editMode || viewMode);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    workOrderNumber: '',
    title: '',
    description: '',
    priority: 'medium',
    status: 'draft',
    category: '',
    workType: '',
    location: '',
    siteAddress: '',
    
    // Assignment & Resources
    assignedTo: '',
    projectManager: '',
    subcontractor: '',
    teamMembers: [],
    requiredSkills: [],
    equipment: [],
    materials: [],
    
    // Timeline & Scheduling
    startDate: '',
    endDate: '',
    estimatedDuration: '',
    actualStartDate: '',
    actualEndDate: '',
    deadline: '',
    workingHours: '',
    timeZone: '',
    
    // Budget & Costs
    budget: '',
    currency: 'USD',
    hourlyRate: '',
    estimatedCost: '',
    actualCost: '',
    costBreakdown: {
      labor: '',
      materials: '',
      equipment: '',
      travel: '',
      other: ''
    },
    
    // Work Details
    scopeOfWork: '',
    deliverables: [],
    milestones: [],
    requirements: [],
    specifications: '',
    qualityStandards: '',
    
    // Safety & Compliance
    safetyRequirements: [],
    complianceRequirements: [],
    permits: [],
    inspections: [],
    safetyEquipment: [],
    
    // Communication & Documentation
    communicationChannels: [],
    reportingFrequency: 'weekly',
    progressUpdates: true,
    documentation: [],
    attachments: [],
    photos: [],
    
    // Risk Management
    risks: [],
    mitigationPlans: [],
    contingencyPlans: [],
    
    // Advanced Settings
    autoNotifications: true,
    progressTracking: true,
    budgetAlerts: true,
    deadlineReminders: true,
    customFields: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to get disabled/readOnly props for form inputs
  const getInputProps = () => ({
    disabled: viewMode,
    readOnly: viewMode
  });

  // Load work order data if in edit or view mode
  useEffect(() => {
    const loadWorkOrderData = async () => {
      if (workOrderId) {
        setLoading(true);
        try {
          const response = await WorkOrderService.getWorkOrder(workOrderId);
          if (response && response.success) {
            const mappedData = mapWorkOrderToFormData(response.data);
            if (mappedData) {
              setFormData(mappedData);
            }
          } else {
            showError('Failed to load work order details.');
            navigate('/subcontracting/work-orders');
          }
        } catch (err) {
          console.error('Error loading work order:', err);
          showError('Failed to load work order details.');
          navigate('/subcontracting/work-orders');
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadWorkOrderData();
  }, [workOrderId]);

  // Load test mode setting on component mount (only in create mode)
  useEffect(() => {
    if (!editMode && !viewMode) {
      const loadTestMode = async () => {
        try {
          const response = await fetch(API_CONFIG.ENDPOINTS.SETTINGS, {
            headers: API_CONFIG.HEADERS
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              setTestMode(result.data.testMode ?? false);
            }
          }
        } catch (error) {
          console.error('Error loading test mode setting:', error);
        }
      };
      
      loadTestMode();
    }
  }, [editMode, viewMode]);
  
  const loadSubcontractors = async () => {
    setLoadingSubcontractors(true);
    try {
      const response = await VendorService.getVendors({ limit: 100 });

      if (response?.success) {
        setSubcontractors(response.data || []);
      } else {
        setSubcontractors([]);
      }
    } catch (err) {
      console.error('Error loading subcontractors:', err);
      setSubcontractors([]);
    } finally {
      setLoadingSubcontractors(false);
    }
  };

  useEffect(() => {
    loadSubcontractors();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.USERS, {
        headers: API_CONFIG.HEADERS
      });

      const result = await response.json();
      if (result?.success) {
        setUsers(result.data || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    }
  };
  
  useEffect(() => {
    loadUsers();
  }, []);

  // Function to fill form with test data
  const fillTestData = () => {
    const testData = {
      workOrderNumber: 'WO-2024-001',
      title: 'Office Building HVAC System Maintenance',
      description: 'Comprehensive maintenance and inspection of HVAC systems in the main office building. Includes filter replacement, duct cleaning, and system calibration.',
      priority: 'high',
      status: 'pending',
      category: 'maintenance',
      workType: 'preventive',
      location: 'Main Office Building',
      siteAddress: '1234 Business Park Drive, Suite 200, Los Angeles, CA 90001',
      
      assignedTo: '',
      projectManager: '',
      subcontractor: '',
      teamMembers: [],
      requiredSkills: ['HVAC Certification', 'Electrical Safety', 'System Diagnostics'],
      equipment: ['HVAC Tools', 'Multimeter', 'Safety Equipment'],
      materials: ['Air Filters', 'Refrigerant', 'Cleaning Supplies'],
      
      startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
      endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 10 days from now
      estimatedDuration: '3 days',
      deadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 14 days from now
      workingHours: '8:00 AM - 5:00 PM',
      timeZone: 'PST',
      
      budget: '5000.00',
      currency: 'USD',
      hourlyRate: '125.00',
      estimatedCost: '4500.00',
      actualCost: '0.00',
      costBreakdown: {
        labor: '3000.00',
        materials: '1000.00',
        equipment: '400.00',
        travel: '100.00',
        other: '0.00'
      },
      
      scopeOfWork: 'Complete HVAC system maintenance including: 1) Replace all air filters, 2) Clean and inspect ductwork, 3) Calibrate thermostats, 4) Check refrigerant levels, 5) Test all system components, 6) Document findings and recommendations.',
      deliverables: ['Maintenance Report', 'System Performance Data', 'Recommendations Document'],
      milestones: ['Initial Inspection', 'Filter Replacement', 'System Calibration', 'Final Testing'],
      requirements: ['Access to all HVAC units', 'Building access during business hours', 'Safety equipment provided'],
      specifications: 'All work must comply with OSHA safety standards and local building codes. Use only certified HVAC parts and materials.',
      qualityStandards: 'Work must meet or exceed manufacturer specifications. All systems must pass performance testing before completion.',
      
      safetyRequirements: ['Hard hats required', 'Safety glasses mandatory', 'Proper ventilation', 'Lockout/tagout procedures'],
      complianceRequirements: ['OSHA Compliance', 'Building Code Compliance', 'Environmental Regulations'],
      permits: ['Building Access Permit', 'HVAC Work Permit'],
      inspections: ['Pre-work Safety Inspection', 'Post-work Quality Inspection'],
      safetyEquipment: ['Hard Hats', 'Safety Glasses', 'Gloves', 'Respirators'],
      
      communicationChannels: ['Email', 'Phone', 'Project Portal'],
      reportingFrequency: 'daily',
      progressUpdates: true,
      documentation: [],
      attachments: [],
      photos: [],
      
      risks: ['System downtime', 'Weather delays', 'Parts availability'],
      mitigationPlans: ['Schedule during low-usage periods', 'Order parts in advance', 'Have backup equipment ready'],
      contingencyPlans: ['Emergency repair procedures', 'Alternative scheduling options'],
      
      autoNotifications: true,
      progressTracking: true,
      budgetAlerts: true,
      deadlineReminders: true,
      customFields: []
    };
    
    setFormData(testData);
    success('Test data filled successfully!');
  };

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Work Order Details',
      icon: <FileText size={18} />
    },
    {
      id: 'assignment',
      label: 'Assignment & Resources',
      icon: <Users size={18} />
    },
    {
      id: 'timeline',
      label: 'Timeline & Budget',
      icon: <Calendar size={18} />
    },
    {
      id: 'compliance',
      label: 'Safety & Compliance',
      icon: <Shield size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    // Ensure that object values are converted to strings for select fields
    let sanitizedValue = value;
    if (typeof value === 'object' && value !== null) {
      if (value._id) {
        sanitizedValue = value._id;
      } else {
        sanitizedValue = '';
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
  };

  // Helper function to safely get string value for select inputs
  const getSelectValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value._id || '';
    }
    return String(value);
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value && value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      if (!formData.workOrderNumber || !formData.title || !formData.startDate) {
        showError('Please fill in all required fields (Work Order Number, Title, and Start Date).');
        setIsSubmitting(false);
        return;
      }

      // Format data for backend - convert empty strings to undefined for optional fields
      const workOrderData = {
        workOrderNumber: formData.workOrderNumber.trim(),
        title: formData.title.trim(),
        description: formData.description?.trim() || undefined,
        priority: formData.priority || 'medium',
        status: formData.status || 'draft',
        category: formData.category?.trim() || undefined,
        workType: formData.workType?.trim() || undefined,
        location: formData.location?.trim() || undefined,
        siteAddress: formData.siteAddress?.trim() || undefined,
        
        assignedTo: formData.assignedTo || undefined,
        projectManager: formData.projectManager || undefined,
        subcontractor: formData.subcontractor || undefined,
        teamMembers: formData.teamMembers?.length > 0 ? formData.teamMembers : undefined,
        requiredSkills: formData.requiredSkills?.length > 0 ? formData.requiredSkills : undefined,
        equipment: formData.equipment?.length > 0 ? formData.equipment : undefined,
        materials: formData.materials?.length > 0 ? formData.materials : undefined,
        
        startDate: formData.startDate ? new Date(formData.startDate) : undefined,
        endDate: formData.endDate ? new Date(formData.endDate) : undefined,
        estimatedDuration: formData.estimatedDuration?.trim() || undefined,
        deadline: formData.deadline ? new Date(formData.deadline) : undefined,
        workingHours: formData.workingHours?.trim() || undefined,
        timeZone: formData.timeZone?.trim() || undefined,
        
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
        currency: formData.currency || 'USD',
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : undefined,
        estimatedCost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : undefined,
        actualCost: formData.actualCost ? parseFloat(formData.actualCost) : undefined,
        costBreakdown: formData.costBreakdown ? {
          labor: formData.costBreakdown.labor ? parseFloat(formData.costBreakdown.labor) : 0,
          materials: formData.costBreakdown.materials ? parseFloat(formData.costBreakdown.materials) : 0,
          equipment: formData.costBreakdown.equipment ? parseFloat(formData.costBreakdown.equipment) : 0,
          travel: formData.costBreakdown.travel ? parseFloat(formData.costBreakdown.travel) : 0,
          other: formData.costBreakdown.other ? parseFloat(formData.costBreakdown.other) : 0
        } : undefined,
        
        scopeOfWork: formData.scopeOfWork?.trim() || undefined,
        deliverables: formData.deliverables?.length > 0 ? formData.deliverables : undefined,
        milestones: formData.milestones?.length > 0 ? formData.milestones : undefined,
        requirements: formData.requirements?.length > 0 ? formData.requirements : undefined,
        specifications: formData.specifications?.trim() || undefined,
        qualityStandards: formData.qualityStandards?.trim() || undefined,
        
        safetyRequirements: formData.safetyRequirements?.length > 0 ? formData.safetyRequirements : undefined,
        complianceRequirements: formData.complianceRequirements?.length > 0 ? formData.complianceRequirements : undefined,
        permits: formData.permits?.length > 0 ? formData.permits : undefined,
        inspections: formData.inspections?.length > 0 ? formData.inspections : undefined,
        safetyEquipment: formData.safetyEquipment?.length > 0 ? formData.safetyEquipment : undefined,
        
        communicationChannels: formData.communicationChannels?.length > 0 ? formData.communicationChannels : undefined,
        reportingFrequency: formData.reportingFrequency || 'weekly',
        progressUpdates: formData.progressUpdates !== undefined ? formData.progressUpdates : true,
        
        risks: formData.risks?.length > 0 ? formData.risks : undefined,
        mitigationPlans: formData.mitigationPlans?.length > 0 ? formData.mitigationPlans : undefined,
        contingencyPlans: formData.contingencyPlans?.length > 0 ? formData.contingencyPlans : undefined,
        
        autoNotifications: formData.autoNotifications !== undefined ? formData.autoNotifications : true,
        progressTracking: formData.progressTracking !== undefined ? formData.progressTracking : true,
        budgetAlerts: formData.budgetAlerts !== undefined ? formData.budgetAlerts : true,
        deadlineReminders: formData.deadlineReminders !== undefined ? formData.deadlineReminders : true
      };

      // Remove undefined values
      Object.keys(workOrderData).forEach(key => {
        if (workOrderData[key] === undefined) {
          delete workOrderData[key];
        }
      });

      // Create or update work order using the service
      let response;
      if (editMode && workOrderId) {
        response = await WorkOrderService.updateWorkOrder(workOrderId, workOrderData);
        if (response && response.success) {
          success('Work Order updated successfully!');
          setTimeout(() => {
            navigate('/subcontracting/work-orders');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to update work order. Please try again.');
        }
      } else {
        response = await WorkOrderService.createWorkOrder(workOrderData);
        if (response && response.success) {
          success('Work Order created successfully!');
          setTimeout(() => {
            navigate('/subcontracting/work-orders');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to create work order. Please try again.');
        }
      }
    } catch (err) {
      console.error('Error creating work order:', err);
      console.error('Error response data:', err?.response?.data);
      console.error('Error response errors:', err?.response?.data?.errors);
      
      // Handle error from service
      let errorMessage = 'Failed to create work order. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        // Check for validation errors array
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map(e => {
            const field = e.param || e.path || '';
            const msg = e.msg || e.message || 'Invalid value';
            return field ? `${field}: ${msg}` : msg;
          });
          errorMessage = `Validation failed: ${validationErrors.join(', ')}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderWorkOrderDetails();
      case 'assignment':
        return renderAssignmentResources();
      case 'timeline':
        return renderTimelineBudget();
      case 'compliance':
        return renderSafetyCompliance();
      default:
        return renderWorkOrderDetails();
    }
  };

  const renderWorkOrderDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Work Order Information</h3>
        <p className="section-description">Basic work order details and specifications</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Work Order Number *</label>
          <input
            type="text"
            className={`form-control ${viewMode ? 'view-mode' : ''}`}
            value={formData.workOrderNumber}
            onChange={(e) => handleInputChange('workOrderNumber', e.target.value)}
            placeholder="WO-2024-001"
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Title *</label>
          <input
            type="text"
            className={`form-control ${viewMode ? 'view-mode' : ''}`}
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter work order title"
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className={`form-select ${viewMode ? 'view-mode' : ''}`}
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            {...getInputProps()}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className={`form-select ${viewMode ? 'view-mode' : ''}`}
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            {...getInputProps()}
          >
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Category</label>
          <select
            className="form-select"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
          >
            <option value="">Select category</option>
            <option value="construction">Construction</option>
            <option value="maintenance">Maintenance</option>
            <option value="repair">Repair</option>
            <option value="installation">Installation</option>
            <option value="inspection">Inspection</option>
            <option value="cleaning">Cleaning</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Work Type</label>
          <select
            className="form-select"
            value={formData.workType}
            onChange={(e) => handleInputChange('workType', e.target.value)}
          >
            <option value="">Select work type</option>
            <option value="scheduled">Scheduled</option>
            <option value="emergency">Emergency</option>
            <option value="preventive">Preventive</option>
            <option value="corrective">Corrective</option>
            <option value="routine">Routine</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Location</label>
          <input
            type="text"
            className="form-control"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            placeholder="Enter work location"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Site Address</label>
          <input
            type="text"
            className="form-control"
            value={formData.siteAddress}
            onChange={(e) => handleInputChange('siteAddress', e.target.value)}
            placeholder="Enter site address"
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className={`form-control ${viewMode ? 'view-mode' : ''}`}
          rows="4"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Detailed description of the work to be performed"
          {...getInputProps()}
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Scope of Work</label>
        <textarea
          className={`form-control ${viewMode ? 'view-mode' : ''}`}
          rows="4"
          value={formData.scopeOfWork}
          onChange={(e) => handleInputChange('scopeOfWork', e.target.value)}
          placeholder="Define the scope and boundaries of the work"
          {...getInputProps()}
        />
      </div>
    </div>
  );

  const renderAssignmentResources = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Assignment & Resources</h3>
        <p className="section-description">Assign work order to team members and allocate resources</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Assigned To *</label>
          <select
          className="form-select"
          value={formData.assignedTo}
          onChange={(e) => handleInputChange('assignedTo', e.target.value)}
          >
          <option value="">Select assignee</option>
          {users.map(user => (
            <option key={user._id} value={user._id}>
              {user.name || user.email}
            </option>
          ))}
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Project Manager</label>
          <select
          className="form-select"
          value={formData.projectManager}
          onChange={(e) => handleInputChange('projectManager', e.target.value)}
          >
            <option value="">Select project manager</option>
            {users.map(user => (
              <option key={user._id} value={user._id}>
                {user.name || user.email}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Subcontractor</label>
          <select
  className="form-select"
  value={getSelectValue(formData.subcontractor)}
  onChange={(e) => handleInputChange('subcontractor', e.target.value)}
  disabled={loadingSubcontractors}
  {...getInputProps()}
>
  <option value="">Select subcontractor</option>

  {subcontractors.map((vendor) => (
    <option
      key={vendor._id || vendor.vendorId}
      value={String(vendor._id || vendor.vendorId)}
    >
      {vendor.name}
      {vendor.vendorId ? ` (${vendor.vendorId})` : ''}
    </option>
  ))}
</select>

        </div>
        
        <div className="form-group">
          <label className="form-label">Working Hours</label>
          <input
            type="text"
            className="form-control"
            value={formData.workingHours}
            onChange={(e) => handleInputChange('workingHours', e.target.value)}
            placeholder="e.g., 8 AM - 5 PM"
            {...getInputProps()}
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Required Skills</label>
        <div className="array-input-group">
          {formData.requiredSkills.map((skill, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={typeof skill === 'string' ? skill : (skill?._id || skill?.name || String(skill || ''))}
                onChange={(e) => {
                  const newSkills = [...formData.requiredSkills];
                  newSkills[index] = e.target.value;
                  setFormData(prev => ({ ...prev, requiredSkills: newSkills }));
                }}
                placeholder="Enter required skill"
              />
              {!viewMode && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleArrayRemove('requiredSkills', index)}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
          {!viewMode && (
            <div className="array-input-add">
              <input
                type="text"
                className="form-control"
                placeholder="Add new skill"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleArrayAdd('requiredSkills', e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={(e) => {
                  const input = e.target.previousElementSibling;
                  if (input && input.value) {
                    handleArrayAdd('requiredSkills', input.value);
                    input.value = '';
                  }
                }}
              >
                <Plus size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Equipment Needed</label>
        <div className="array-input-group">
          {formData.equipment.map((item, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={typeof item === 'string' ? item : (item?._id || item?.name || String(item || ''))}
                onChange={(e) => {
                  const newEquipment = [...formData.equipment];
                  newEquipment[index] = e.target.value;
                  setFormData(prev => ({ ...prev, equipment: newEquipment }));
                }}
                placeholder="Enter equipment"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('equipment', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new equipment"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('equipment', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('equipment', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTimelineBudget = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Timeline & Budget</h3>
        <p className="section-description">Schedule work order and manage budget allocation</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Start Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Estimated Duration</label>
          <input
            type="text"
            className="form-control"
            value={formData.estimatedDuration}
            onChange={(e) => handleInputChange('estimatedDuration', e.target.value)}
            placeholder="e.g., 2 days, 1 week"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Deadline</label>
          <input
            type="date"
            className="form-control"
            value={formData.deadline}
            onChange={(e) => handleInputChange('deadline', e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Budget</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              className="form-control"
              value={formData.budget}
              onChange={(e) => handleInputChange('budget', e.target.value)}
              placeholder="0.00"
              step="0.01"
              {...getInputProps()}
              min="0"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Currency</label>
          <select
            className="form-select"
            value={formData.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="CAD">CAD</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Hourly Rate</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              className="form-control"
              value={formData.hourlyRate}
              onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Estimated Cost</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              className="form-control"
              value={formData.estimatedCost}
              onChange={(e) => handleInputChange('estimatedCost', e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Cost Breakdown</label>
        <div className="form-grid-5">
          <div className="form-group">
            <label className="form-label">Labor</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control"
                value={formData.costBreakdown.labor}
                onChange={(e) => handleNestedInputChange('costBreakdown', 'labor', e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Materials</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control"
                value={formData.costBreakdown.materials}
                onChange={(e) => handleNestedInputChange('costBreakdown', 'materials', e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Equipment</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control"
                value={formData.costBreakdown.equipment}
                onChange={(e) => handleNestedInputChange('costBreakdown', 'equipment', e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Travel</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control"
                value={formData.costBreakdown.travel}
                onChange={(e) => handleNestedInputChange('costBreakdown', 'travel', e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Other</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control"
                value={formData.costBreakdown.other}
                onChange={(e) => handleNestedInputChange('costBreakdown', 'other', e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSafetyCompliance = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Safety & Compliance</h3>
        <p className="section-description">Safety requirements, compliance standards, and risk management</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Safety Requirements</label>
        <div className="array-input-group">
          {formData.safetyRequirements.map((requirement, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={typeof requirement === 'string' ? requirement : (requirement?._id || requirement?.name || String(requirement || ''))}
                onChange={(e) => {
                  const newRequirements = [...formData.safetyRequirements];
                  newRequirements[index] = e.target.value;
                  setFormData(prev => ({ ...prev, safetyRequirements: newRequirements }));
                }}
                placeholder="Enter safety requirement"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('safetyRequirements', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new safety requirement"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('safetyRequirements', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('safetyRequirements', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Compliance Requirements</label>
        <div className="array-input-group">
          {formData.complianceRequirements.map((requirement, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={typeof requirement === 'string' ? requirement : (requirement?._id || requirement?.name || String(requirement || ''))}
                onChange={(e) => {
                  const newRequirements = [...formData.complianceRequirements];
                  newRequirements[index] = e.target.value;
                  setFormData(prev => ({ ...prev, complianceRequirements: newRequirements }));
                }}
                placeholder="Enter compliance requirement"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('complianceRequirements', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new compliance requirement"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('complianceRequirements', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('complianceRequirements', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Required Permits</label>
        <div className="array-input-group">
          {formData.permits.map((permit, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={typeof permit === 'string' ? permit : (permit?._id || permit?.name || String(permit || ''))}
                onChange={(e) => {
                  const newPermits = [...formData.permits];
                  newPermits[index] = e.target.value;
                  setFormData(prev => ({ ...prev, permits: newPermits }));
                }}
                placeholder="Enter permit requirement"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('permits', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new permit"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('permits', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('permits', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Quality Standards</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.qualityStandards}
          onChange={(e) => handleInputChange('qualityStandards', e.target.value)}
          placeholder="Define quality standards and acceptance criteria"
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title={viewMode ? "View Work Order" : editMode ? "Edit Work Order" : "Create Work Order"}
          description={viewMode ? "View work order details and specifications" : editMode ? "Update work order details and specifications" : "Create a comprehensive work order with detailed specifications and requirements"}
        />
        <div className="page-container" style={{ width: '100%', maxWidth: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title={viewMode ? "View Work Order" : editMode ? "Edit Work Order" : "Create Work Order"}
        description={viewMode ? "View work order details and specifications" : editMode ? "Update work order details and specifications" : "Create a comprehensive work order with detailed specifications and requirements"}
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }} className={viewMode ? 'view-mode' : ''}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {renderTabContent()}
          </div>

          {/* Form Actions */}
          <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {testMode && !editMode && !viewMode && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={fillTestData}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    backgroundColor: '#ffffff',
                    borderColor: '#d1d5db'
                  }}
                >
                  <Database size={18} />
                  Fill Test Data
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {viewMode ? (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/subcontracting/work-orders')}
                  >
                    <ArrowLeft size={18} />
                    Back to List
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/subcontracting/work-orders/edit/${workOrderId}`)}
                    disabled={!workOrderId || viewMode}
                  >
                    <Edit size={18} />
                    Edit Work Order
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/subcontracting/work-orders')}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || viewMode}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        {editMode ? 'Updating Work Order...' : 'Creating Work Order...'}
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        {editMode ? 'Update Work Order' : 'Create Work Order'}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {viewMode && (
        <style>{`
          .view-mode input,
          .view-mode select,
          .view-mode textarea {
            pointer-events: none;
            opacity: 0.7;
            background-color: #f3f4f6 !important;
            cursor: not-allowed;
          }
          .view-mode .array-input-add,
          .view-mode .btn-outline-danger {
            display: none !important;
          }
          .view-mode button[type="submit"] {
            display: none !important;
          }
        `}</style>
      )}
    </>
  );
};

export default CreateWorkOrder;